import { formatDistanceToNow, format, differenceInCalendarDays } from "date-fns";

export function formatRelative(date) {
  if (!date) return "—";
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  } catch {
    return "—";
  }
}

export function formatDate(date, fmt = "dd MMM yyyy") {
  if (!date) return "—";
  try {
    return format(new Date(date), fmt);
  } catch {
    return "—";
  }
}

export function formatDateTime(date) {
  return formatDate(date, "dd MMM yyyy, hh:mm a");
}

const DAY_MS = 24 * 60 * 60 * 1000;

// "Closing soon" means within 72 hours — the same window the /stats
// closing_soon count and the closing_within_days list filter use.
export const CLOSING_SOON_DAYS = 3;

function msUntil(date) {
  if (!date) return null;
  const ms = new Date(date).getTime() - Date.now();
  return Number.isNaN(ms) ? null : ms;
}

// Whole days left, negative once the date has passed. differenceInDays
// truncated toward zero, so a tender that closed five hours ago read as 0
// days left and showed "Closing Soon".
export function daysUntil(date) {
  const ms = msUntil(date);
  return ms === null ? null : Math.floor(ms / DAY_MS);
}

export function isClosingSoon(date) {
  const ms = msUntil(date);
  return ms !== null && ms >= 0 && ms <= CLOSING_SOON_DAYS * DAY_MS;
}

// The countdown shown under a closing date: { text, urgent }, or null when the
// tender has closed or has no date. Wording follows the calendar ("Closes
// tomorrow" at 8 pm for a 9 am deadline); urgency follows the 72-hour window.
export function deadlineCountdown(date) {
  const ms = msUntil(date);
  if (ms === null || ms < 0) return null;
  const calendarDays = differenceInCalendarDays(new Date(date), new Date());
  const urgent = isClosingSoon(date);
  if (calendarDays <= 0) return { text: "Closes today", urgent };
  if (calendarDays === 1) return { text: "Closes tomorrow", urgent };
  return { text: `${calendarDays} days left`, urgent };
}

export function getTenderStatus(closingDate) {
  if (!closingDate) return { label: "Unknown", type: "neutral" };
  const ms = msUntil(closingDate);
  if (ms === null) return { label: "Unknown", type: "neutral" };
  // Closed is a normal end state, not an error — neutral, not red.
  if (ms < 0) return { label: "Closed", type: "neutral" };
  if (isClosingSoon(closingDate)) return { label: "Closing Soon", type: "warning" };
  // The day count lives in the deadline countdown; repeating it here as
  // "5 days left" said the same thing twice in one row.
  return { label: "Active", type: "success" };
}
